import { connect, ConnectedProps } from "react-redux";
import { RootState, AppDispatch, store } from "store";
import TermsAndConditions from "./TermsAndConditions";
import { actions as notificationActions } from "reducers/notification";
import { actions as termsActions } from "reducers/termsAndConditions";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import "./i18n";
import { NotificationStateType } from "reducers/types/notification";
import { history } from "reducers";
import { dirtyAlert } from "utils/alerts";
import { toast } from "@pickit/pickit-components";

const TermsAndConditionsContainer = (props: ConnectorProps) => {
  const { t } = useTranslation("termsAndConditions");
  const [currentFile, setCurrentFile] = useState<{ url: string; name?: string; file?: File } | null>(null);
  const lastErrorMessageRef = useRef<string | null>(null);

  useEffect(() => {
    props.getTermsVersions(props.countryCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileUpload = async (file: File) => {
    try {
      if (props.fileStatus.error) {
        store.dispatch(termsActions.setFileStatus({ 
          loading: false, 
          error: false, 
          message: "" 
        }));
      }
      
      const url = URL.createObjectURL(file);
      setCurrentFile({ url: url, name: file.name, file: file });
    } catch (error) {
      store.dispatch(termsActions.setFileStatus({ loading: false, error: true, message: t("errorMessage") }));
      toast({
        body: t("errorMessage"),
        type: "error",
      });
    }
  };

  const handleUpdateClick = () => {
    const fileName = currentFile?.file?.name || currentFile?.name || "term-cond.pdf";
    props.showNotification({
      title: t("confirmUpdateTitle"),
      body: t("confirmUpdateMessage", { fileName }),
      type: "warning",
      onConfirm: handleUpdate,
      onConfirmLabel: "global:confirm",
    });
  };

  const handleDeleteClick = () => {
    props.showNotification({
      title: t("confirmDeleteTitle"),
      body: t("confirmDeleteMessage"),
      type: "warning",
      onConfirm: goToPreviousFile,
      onCancel: () => {},
      onConfirmLabel: "global:deleteConfirmation",
    });
  };

  const handleUpdate = () => {
    if (!currentFile || !currentFile.file) {
      console.error("No file available");
      return;
    }

    store.dispatch(termsActions.setFileStatus({ loading: true, error: false }));
    props.uploadTermsVersion(props.countryCode, currentFile.file);
    
  };

  const goToPreviousFile = () => {
    setCurrentFile(null);
    props.setFileStatus({
      error: false,
      loading: false,
      message: "",
      justUploaded: false,
    });
  };

  const downloadCurrentFile = () => {
    if (currentFile?.url) {
      const link = document.createElement("a");
      link.href = currentFile.url;
      link.download = currentFile.name || "terminos-y-condiciones.pdf";
      link.click();
    }
  };

  const downloadVersion = async (versionId: string, url: string, fileName: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      const downloadFileName = fileName && fileName !== "file.pdf" && fileName.trim() !== ""
        ? fileName
        : `terminos-y-condiciones-v${versionId}.pdf`;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast({
        body: t("termsAndConditions:errorDownload") || "Error al descargar el archivo",
        type: "error",
      });
    }
  };

  const viewVersion = (url: string) => {
    window.open(url, '_blank');
  };

  useEffect(() => {
    if (props.fileStatus.justUploaded) {
      toast({
        body: t("successMessage"),
        type: "success",
      });
      setCurrentFile(null);
      const timeoutId = setTimeout(() => {
        props.clearFileStatus();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fileStatus.justUploaded]);

  useEffect(() => {
    if (props.fileStatus.error && props.fileStatus.message) {
      const isFileValidationError = props.fileStatus.message.includes("El formato del archivo debe ser PDF");
      
      if (!isFileValidationError) {
        const currentMessage = props.fileStatus.message;
        if (lastErrorMessageRef.current !== currentMessage) {
          lastErrorMessageRef.current = currentMessage;
          toast({
            body: currentMessage || t("errorMessage"),
            type: "error",
          });
        }
        setTimeout(() => {
          props.clearFileStatus();
          lastErrorMessageRef.current = null;
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.fileStatus.error, props.fileStatus.message]);
  
  useEffect(() => {
    if (currentFile && props.fileStatus.error) {
      const isFileValidationError = props.fileStatus.message?.includes("El formato del archivo debe ser PDF");
      if (isFileValidationError) {
        store.dispatch(termsActions.setFileStatus({ 
          loading: false, 
          error: false, 
          message: "" 
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFile]);

  const handleFileTypeError = (message: string) => {
    if (!message || message === "") {
      store.dispatch(termsActions.setFileStatus({ 
        loading: false, 
        error: false, 
        message: ""
      }));
      return;
    }
    
    const errorMessage = t("termsAndConditions:invalidFileType") || message;
    store.dispatch(termsActions.setFileStatus({ 
      loading: false, 
      error: true, 
      message: errorMessage
    }));
  };

  useEffect(() => {
    if (!currentFile) {
      return;
    }

    let pendingLocation: any = null;

    const unblock = history.block((location, action) => {
      if (currentFile) {
        pendingLocation = location;
        const alert = dirtyAlert({
          onCancel: () => {
            goToPreviousFile();
            unblock();
            if (pendingLocation) {
              history.push(pendingLocation.pathname + (pendingLocation.search || ""));
            }
          },
          cancelLabel: t("global:notSave"),
          scroll: false,
        });
        
        props.showNotification({
          title: alert.title || "",
          body: alert.body || "",
          type: alert.level || "warning",
          onConfirm: alert.onConfirm,
          onCancel: alert.onCancel,
          onConfirmLabel: alert.onConfirmLabel,
          onCancelLabel: alert.onCancelLabel,
        });
        return false;
      }
      return undefined;
    });

    return () => {
      unblock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFile]);

  return (
    <TermsAndConditions
      testId="terms-and-conditions"
      currentFile={currentFile}
      fileStatus={props.fileStatus}
      termsVersions={props.versions}
      loading={props.loading}
      fileHandler={handleFileUpload}
      goToPreviousFile={goToPreviousFile}
      downloadCurrentFile={downloadCurrentFile}
      downloadVersion={downloadVersion}
      viewVersion={viewVersion}
      onUpdate={handleUpdateClick}
      onDeleteFile={handleDeleteClick}
      onFileTypeError={handleFileTypeError}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    versions: state.termsAndConditions?.versions || [],
    loading: state.termsAndConditions?.loading || false,
    error: state.termsAndConditions?.error || null,
    currentFile: state.termsAndConditions?.currentFile || null,
    fileStatus: state.termsAndConditions?.fileStatus || {
      loading: false,
      error: false,
      message: "",
      justUploaded: false,
    },
    countryCode: state.session?.countryCode || "AR",
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  showNotification: (notification: {
    title: string;
    body?: string;
    type: "success" | "error" | "warning" | "info";
    onConfirm?: Function;
    onCancel?: Function;
    onConfirmLabel?: string;
    onCancelLabel?: string;
  }) => {
    dispatch(notificationActions.showNotification({
      title: notification.title,
      level: notification.type as NotificationStateType["level"],
      body: notification.body || "",
      onConfirmLabel: notification.onConfirmLabel || "global:confirm",
      onCancelLabel: notification.onCancelLabel || "global:cancel",
      onConfirm: notification.onConfirm,
      onCancel: notification.onCancel,
    }));
  },
  getTermsVersions: (countryCode: string) => {
    dispatch({ type: "termsAndConditions/getTermsVersions", payload: { countryCode } });
  },
  uploadTermsVersion: (countryCode: string, file: File) => {
    if (!countryCode) {
      return;
    }
    
    dispatch({ 
      type: "termsAndConditions/uploadTermsVersion", 
      payload: { countryCode, file } 
    });
  },
  clearFileStatus: () => {
    dispatch(termsActions.clearFileStatus());
  },
  setFileStatus: (fileStatus: { 
    loading: boolean; 
    error: boolean; 
    message?: string;
    justUploaded?: boolean;
  }) => {
    dispatch(termsActions.setFileStatus(fileStatus));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(TermsAndConditionsContainer);

